import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  getVolunteerDashboard,
  deleteVolunteerSignup,
  getVolunteerOpportunitiesSummary,
} from 'api/volunteerApi';
import styles from './DashboardVolunteer.module.css';
import { toast } from 'react-toastify';
import Loading from 'components/common/Loading';
import { useNavigate } from 'react-router-dom';
import routes from 'routes';

const DashboardVolunteer = ({ clubId }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [opportunitiesData, setOpportunitiesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentCancelRequest, setCurrentCancelRequest] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await getVolunteerDashboard(clubId);

        if (!response.data || !response.data.userStats) {
          throw new Error('Invalid dashboard data structure');
        }

        setDashboardData(response.data);

        const opportunitiesResponse =
          await getVolunteerOpportunitiesSummary(clubId);
        setOpportunitiesData(opportunitiesResponse.data);

        setLoading(false);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        toast.error(err.message || 'Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [clubId]);

  const handleShowCancelModal = (signupId, meetName, roleName) => {
    setCurrentCancelRequest({ signupId, meetName, roleName });
    setShowCancelModal(true);
  };

  const handleCloseModal = () => {
    setShowCancelModal(false);
    setCurrentCancelRequest(null);
  };

  const handleConfirmCancel = async () => {
    if (currentCancelRequest) {
      try {
        await deleteVolunteerSignup(currentCancelRequest.signupId);

        setDashboardData((prev) => ({
          ...prev,
          upcomingCommitments: prev.upcomingCommitments.filter(
            (item) => item.volunteerSignupID !== currentCancelRequest.signupId
          ),
          userStats: {
            ...prev.userStats,
            upcomingCommitments: prev.userStats.upcomingCommitments - 1,
          },
        }));

        toast.info(
          `Your volunteer signup for ${currentCancelRequest.roleName} at the ${currentCancelRequest.meetName} has been cancelled.`
        );
        handleCloseModal();
      } catch (err) {
        toast.error(
          err.message || 'Failed to cancel volunteer signup. Please try again.'
        );
      } finally {
        handleCloseModal();
      }
    }
  };

  const handleViewOpportunities = (meetId) => {
    navigate(routes.admin.showMeet.url(clubId, meetId));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRoleName = (commitment) => {
    const { roleName, programGroupName, groupType } = commitment;

    // For athlete groups and age groups with specific program names, prepend the group name
    if (
      programGroupName &&
      (groupType === 'AthleteGroup' || groupType === 'AgeGroup')
    ) {
      return `${programGroupName}: ${roleName}`;
    }

    // For other cases, just return the role name
    return roleName;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Loading />
      </div>
    );
  }

  if (!dashboardData) {
    return <div className={styles.container}>No data available</div>;
  }

  const { userStats, upcomingCommitments, recentHistory } = dashboardData;

  return (
    <div className={styles.container}>
      {/* Stats Overview */}
      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.upcoming}`}>
          <span className={styles.statNumber}>
            {userStats.upcomingCommitments}
          </span>
          <div className={styles.statLabel}>Upcoming</div>
          <div className={styles.statSublabel}>Commitments</div>
        </div>

        <div className={`${styles.statCard} ${styles.completed}`}>
          <span className={styles.statNumber}>
            {userStats.completedVolunteerJobs}
          </span>
          <div className={styles.statLabel}>Completed</div>
          <div className={styles.statSublabel}>This Season</div>
        </div>

        <div className={`${styles.statCard} ${styles.points}`}>
          <span className={styles.statNumber}>
            {userStats.individualHours || 0}
          </span>
          <div className={styles.statLabel}>Individual Hours</div>
          <div className={styles.statSublabel}>This Season</div>
        </div>

        <div className={`${styles.statCard} ${styles.ranking}`}>
          <span className={styles.statNumber}>{userStats.totalHours || 0}</span>
          <div className={styles.statLabel}>Total Family Hours</div>
          <div className={styles.statSublabel}>This Season</div>
        </div>

        <div className={`${styles.statCard} ${styles.required}`}>
          <span className={styles.statNumber}>{userStats.requiredHours || 0}</span>
          <div className={styles.statLabel}>Required Hours</div>
          <div className={styles.statSublabel}>Family Target This Season</div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* My Upcoming Commitments */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span>📅</span>
            My Upcoming Commitments
          </div>
          <div className={styles.sectionContent}>
            {upcomingCommitments.length > 0 ? (
              upcomingCommitments.map((commitment) => (
                <div
                  className={styles.commitmentItem}
                  key={commitment.volunteerSignupID}
                >
                  <div className={styles.commitmentLeftSection}>
                    <span
                      className={styles.infoIcon}
                      title={commitment.roleDescription}
                    >
                      ℹ️
                    </span>
                    <div className={styles.commitmentInfo}>
                      <div className={styles.meetName}>
                        {commitment.meetName}
                      </div>
                      <div className={styles.commitmentMeta}>
                        {formatDate(commitment.meetDate)} •{' '}
                        {formatTime(commitment.meetDate)}
                      </div>
                    </div>
                    <div className={styles.roleInfo}>
                      <span className={styles.roleName}>
                        {formatRoleName(commitment)}
                      </span>
                      <span className={styles.pointsBadge}>
                        ⭐ {commitment.hoursEarned}{' '}
                        {commitment.hoursEarned === 1 ? 'hour' : 'hours'}
                      </span>
                    </div>
                  </div>

                  <div className={styles.commitmentRightSection}>
                    <span
                      className={`${styles.statusBadge} ${styles[commitment.status?.toLowerCase() || 'registered']}`}
                    >
                      {commitment.statusDisplayName ||
                        commitment.status ||
                        'Registered'}
                    </span>
                    <button
                      className={styles.cancelButton}
                      onClick={() =>
                        handleShowCancelModal(
                          commitment.volunteerSignupID,
                          commitment.meetName,
                          formatRoleName(commitment)
                        )
                      }
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.noItems}>
                <p>No upcoming volunteer commitments</p>
                <p>Ready to help out? Check the opportunities below!</p>
              </div>
            )}
          </div>
        </div>

        {/* Find Opportunities */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span>🔍</span>
            Find Opportunities
          </div>
          <div className={styles.sectionContent}>
            {opportunitiesData.length > 0 ? (
              opportunitiesData.map((opportunity) => (
                <div
                  className={styles.opportunityItem}
                  key={opportunity.meetID}
                >
                  <div className={styles.opportunityLeftSection}>
                    <div className={styles.opportunityInfo}>
                      <div className={styles.meetName}>
                        {opportunity.meetName}
                      </div>
                      <div className={styles.opportunityMeta}>
                        📅 {formatDate(opportunity.meetDate)} • 📍{' '}
                        {opportunity.meetLocation}
                      </div>
                    </div>
                    <div className={styles.staffingInfo}>
                      <span
                        className={`${styles.urgencyBadge} ${styles[opportunity.urgencyLevel]}`}
                      >
                        {opportunity.rolesStillNeeded} needed
                      </span>
                    </div>
                  </div>

                  <div className={styles.opportunityRightSection}>
                    <div className={styles.staffingProgress}>
                      <div className={styles.progressBarContainer}>
                        <div
                          className={`${styles.progressBarFill} ${styles[opportunity.urgencyLevel]}`}
                          style={{
                            width: `${opportunity.staffingPercentage}%`,
                          }}
                        ></div>
                      </div>
                      <span className={styles.progressText}>
                        {opportunity.staffingPercentage}% staffed
                      </span>
                    </div>
                    <button
                      className={styles.viewButton}
                      onClick={() =>
                        handleViewOpportunities(opportunity.meetID)
                      }
                    >
                      View & Sign Up
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.noItems}>
                <p>No volunteer opportunities available at this time</p>
              </div>
            )}
          </div>
        </div>

        {/* Volunteer History */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span>📋</span>
            Volunteer History
          </div>
          <div className={styles.sectionContent}>
            {recentHistory.length > 0 ? (
              recentHistory.map((historyItem, index) => (
                <div
                  className={styles.historyItem}
                  key={historyItem.volunteerSignupID || index}
                >
                  <div className={styles.historyLeftSection}>
                    <div
                      className={`${styles.historyIcon} ${
                        historyItem.status === 'Completed'
                          ? styles.completed
                          : historyItem.status === 'Cancelled'
                            ? styles.cancelled
                            : styles.noshow
                      }`}
                    >
                      {historyItem.status === 'Completed'
                        ? '✓'
                        : historyItem.status === 'Cancelled'
                          ? '✗'
                          : '○'}
                    </div>
                    <div className={styles.historyInfo}>
                      <div className={styles.meetName}>
                        {historyItem.meetName}
                      </div>
                      <div className={styles.historyMeta}>
                        {formatDate(historyItem.meetDate)} •{' '}
                        {formatRoleName(historyItem)}
                      </div>
                    </div>
                  </div>

                  <div className={styles.historyRightSection}>
                    <span
                      className={`${styles.statusBadge} ${styles[historyItem.status?.toLowerCase() || 'unknown']}`}
                    >
                      {historyItem.status || 'Unknown'}
                    </span>
                    <div
                      className={`${styles.pointsEarned} ${
                        historyItem.status === 'Completed'
                          ? styles.positive
                          : ''
                      }`}
                    >
                      {historyItem.status === 'Completed'
                        ? `+${historyItem.hoursEarned || 0} hrs`
                        : '0 hrs'}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.noItems}>
                <p>No volunteer history available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div
          id="cancelModal"
          className={styles.modal}
          onClick={(e) => e.target.id === 'cancelModal' && handleCloseModal()}
        >
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Cancel Volunteer Signup</h2>
              <button onClick={handleCloseModal}>&times;</button>
            </div>

            <div className={styles.modalBody}>
              <p>
                Are you sure you want to cancel your volunteer signup for{' '}
                <strong>{currentCancelRequest?.roleName}</strong> at the{' '}
                <strong>{currentCancelRequest?.meetName}</strong>?
              </p>
              <p>This action cannot be undone.</p>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={handleCloseModal}
              >
                Keep Signup
              </button>
              <button
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleConfirmCancel}
                style={{ background: '#dc3545' }}
              >
                Cancel Signup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DashboardVolunteer.propTypes = {
  clubId: PropTypes.string.isRequired,
};

export default DashboardVolunteer;
